import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Upload, X, Loader2, Star, StarOff, Trash2, GripVertical,
  CheckCircle2, AlertCircle, RefreshCw, Film, ImageIcon,
} from "lucide-react";
import { api, uploadToS3, formatApiError } from "@/lib/api";

/**
 * Full-featured gallery manager for a Wedding.
 * - Loads media directly from /api/media?wedding_id=<id>&purpose=gallery|video
 * - Auto-associates uploads with the wedding on POST /api/media
 * - Thumbnails, per-file progress + status, drag-drop reorder,
 *   set-as-cover, delete, replace, live counts.
 *
 * Props:
 *   weddingId        - required, wedding uuid
 *   weddingSlug      - used as S3 folder segment
 *   currentCoverMediaId
 *   onCoverChange(mediaId, mediaObj) - fires when user clicks star
 *   onCountsChange({photos, videos}) - fires whenever counts change
 *   onDidChange() - fires whenever the manager mutates media (upload/delete/reorder/replace)
 */
export default function WeddingGalleryManager({
  weddingId, weddingSlug, currentCoverMediaId,
  onCoverChange, onCountsChange, onDidChange,
}) {
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]); // per-file: {id, name, kind, status, error, progress}
  const [error, setError] = useState("");
  const replaceTarget = useRef(null); // {mediaId, kind} when user clicks Replace
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);

  const refetch = useCallback(async () => {
    if (!weddingId) return;
    setLoading(true);
    try {
      const [photosRes, videosRes] = await Promise.all([
        api.get("/media", { params: { wedding_id: weddingId, media_type: "image", limit: 500 } }),
        api.get("/media", { params: { wedding_id: weddingId, media_type: "video", limit: 100 } }),
      ]);
      // Include gallery photos + the current cover (if set). Exclude any legacy
      // "cover"-only rows that aren't the current selection.
      const gallery = (photosRes.data.items || [])
        .filter((m) => (m.purpose || "gallery") !== "cover" || m.id === currentCoverMediaId)
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
      const vids = (videosRes.data.items || []).sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
      setPhotos(gallery);
      setVideos(vids);
      onCountsChange?.({ photos: gallery.length, videos: vids.length });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [weddingId, currentCoverMediaId, onCountsChange]);

  useEffect(() => { refetch(); }, [refetch]);

  /* ------------------------------- UPLOADS ------------------------------ */
  const uploadOne = async (file) => {
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const isVideo = file.type.startsWith("video/");
    const kind = isVideo ? "video" : "image";
    setUploads((prev) => [...prev, { id: uid, name: file.name, kind, status: "uploading", error: "" }]);
    try {
      const media = await uploadToS3(file, kind, {
        folder: "weddings",
        weddingSlug,
        purpose: isVideo ? "video" : "gallery",
        association: { wedding_id: weddingId },
      });
      // media returned includes id, url, storage_key, sizes, etc.
      setUploads((prev) => prev.map((u) => u.id === uid ? { ...u, status: "done", media } : u));
      return media;
    } catch (e) {
      const msg = e.message || "Upload failed";
      setUploads((prev) => prev.map((u) => u.id === uid ? { ...u, status: "failed", error: msg } : u));
      return null;
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setError("");
    for (const f of files) {
      await uploadOne(f);
    }
    onDidChange?.();
    await refetch();
  };

  const retryUpload = async (upload) => {
    // Not straightforward without holding the File; require user to re-drop.
    // Instead we clear the failed record — user must re-add.
    setUploads((prev) => prev.filter((u) => u.id !== upload.id));
  };

  const clearFinishedUploads = () => {
    setUploads((prev) => prev.filter((u) => u.status === "uploading" || u.status === "failed"));
  };

  /* ------------------------------- ACTIONS ------------------------------ */
  const deleteMedia = async (m) => {
    if (!window.confirm(`Delete "${m.file_name || 'this image'}"? This removes it from S3 permanently.`)) return;
    try {
      await api.delete(`/media/${m.id}`);
      if (currentCoverMediaId === m.id) onCoverChange?.(null, null);
      onDidChange?.();
      await refetch();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const setAsCover = (m) => {
    onCoverChange?.(m.id, m);
  };

  const clearCover = () => {
    onCoverChange?.(null, null);
  };

  const triggerReplace = (m) => {
    replaceTarget.current = { mediaId: m.id, kind: m.media_type || "image" };
    replaceInputRef.current?.click();
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !replaceTarget.current) return;
    const { mediaId, kind } = replaceTarget.current;
    replaceTarget.current = null;
    // upload new then delete old
    const uploaded = await uploadOne(file);
    if (uploaded) {
      try {
        await api.delete(`/media/${mediaId}`);
        if (currentCoverMediaId === mediaId) onCoverChange?.(uploaded.id, uploaded);
      } catch (_) { /* non-fatal */ }
      await refetch();
    }
  };

  /* ------------------------------- REORDER ------------------------------ */
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragEndPhotos = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = photos.findIndex((p) => p.id === active.id);
    const newIndex = photos.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(photos, oldIndex, newIndex);
    setPhotos(next); // optimistic
    // Persist display_order
    const orderMap = {};
    next.forEach((p, i) => { orderMap[p.id] = i; });
    try {
      await api.post("/media/bulk", { ids: next.map((p) => p.id), action: "reorder", display_order_map: orderMap });
    } catch (e) {
      setError("Reorder failed. Refreshing…");
      refetch();
    }
  };

  const onDragEndVideos = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = videos.findIndex((v) => v.id === active.id);
    const newIndex = videos.findIndex((v) => v.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(videos, oldIndex, newIndex);
    setVideos(next);
    const orderMap = {};
    next.forEach((v, i) => { orderMap[v.id] = i; });
    try {
      await api.post("/media/bulk", { ids: next.map((v) => v.id), action: "reorder", display_order_map: orderMap });
    } catch (e) {
      setError("Reorder failed. Refreshing…");
      refetch();
    }
  };

  /* -------------------------------- RENDER ------------------------------- */
  const activeUploads = uploads.filter((u) => u.status === "uploading");
  const failedUploads = uploads.filter((u) => u.status === "failed");

  return (
    <div className="space-y-6" data-testid="wedding-gallery-manager">
      {/* Upload dropzone */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <label
              data-testid="gm-bulk-upload"
              className="btn-pill btn-primary cursor-pointer !py-2 !px-4 text-[10px]"
            >
              <Upload size={14} /> Bulk upload photos + videos
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                className="hidden"
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
              />
            </label>
            <span className="eyebrow text-[color:var(--text-muted)]" data-testid="gm-counts">
              <ImageIcon size={12} className="inline mr-1" /> {photos.length} photos
              &nbsp;·&nbsp;
              <Film size={12} className="inline mr-1" /> {videos.length} videos
            </span>
          </div>
          {(uploads.length > 0) && (
            <button
              type="button" onClick={clearFinishedUploads}
              className="eyebrow link-underline text-[color:var(--text-muted)]"
              data-testid="gm-clear-uploads"
            >
              Clear log
            </button>
          )}
        </div>

        {/* Live upload progress */}
        {uploads.length > 0 && (
          <div className="space-y-1 mb-3 max-h-48 overflow-y-auto pr-1" data-testid="gm-upload-log">
            {uploads.map((u) => (
              <div key={u.id} className="flex items-center gap-2 text-xs px-2 py-1.5 bg-[color:var(--surface)] border border-[color:var(--border)]">
                {u.status === "uploading" && <Loader2 size={12} className="animate-spin text-[color:var(--accent)]" />}
                {u.status === "done"      && <CheckCircle2 size={12} className="text-emerald-400" />}
                {u.status === "failed"    && <AlertCircle size={12} className="text-red-400" />}
                <span className="truncate flex-1">{u.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)]">{u.kind}</span>
                {u.status === "failed" && (
                  <>
                    <span className="text-red-400 text-[10px] max-w-[220px] truncate" title={u.error}>{u.error}</span>
                    <button type="button" onClick={() => retryUpload(u)} className="text-[color:var(--text-muted)] hover:text-[color:var(--accent)]" title="Dismiss">
                      <X size={12} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-red-400 text-xs mb-3" data-testid="gm-error">{error}</div>
        )}
      </div>

      {/* Photos grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="eyebrow flex items-center gap-2 text-[color:var(--text-muted)]">
            <ImageIcon size={12} /> Photos ({photos.length})
          </div>
          {currentCoverMediaId && (
            <button type="button" onClick={clearCover} className="eyebrow link-underline text-[color:var(--text-muted)]" data-testid="gm-clear-cover">
              Clear cover
            </button>
          )}
        </div>
        {loading ? (
          <div className="py-12 text-center text-[color:var(--text-muted)] text-sm">Loading gallery…</div>
        ) : photos.length === 0 ? (
          <div className="py-12 text-center border border-dashed border-[color:var(--border)] text-[color:var(--text-muted)] text-sm">
            No photos yet — bulk upload above to build the gallery.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndPhotos}>
            <SortableContext items={photos.map((p) => p.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {photos.map((p, idx) => (
                  <PhotoTile
                    key={p.id}
                    media={p}
                    index={idx}
                    isCover={p.id === currentCoverMediaId}
                    onSetCover={() => setAsCover(p)}
                    onDelete={() => deleteMedia(p)}
                    onReplace={() => triggerReplace(p)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Videos row */}
      {videos.length > 0 && (
        <div>
          <div className="eyebrow flex items-center gap-2 mb-3 text-[color:var(--text-muted)]">
            <Film size={12} /> Videos ({videos.length})
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndVideos}>
            <SortableContext items={videos.map((v) => v.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {videos.map((v) => (
                  <VideoTile
                    key={v.id}
                    media={v}
                    onDelete={() => deleteMedia(v)}
                    onReplace={() => triggerReplace(v)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Hidden input for replace */}
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={handleReplaceFile}
      />

      {failedUploads.length > 0 && (
        <div className="text-xs text-[color:var(--text-muted)]">
          {failedUploads.length} upload{failedUploads.length === 1 ? "" : "s"} failed. Re-drop or select those files again to retry.
        </div>
      )}
      {activeUploads.length > 0 && (
        <div className="text-xs text-[color:var(--text-muted)]">
          <Loader2 size={12} className="inline animate-spin mr-1" />
          {activeUploads.length} upload{activeUploads.length === 1 ? "" : "s"} in progress…
        </div>
      )}
    </div>
  );
}

/* ============================================================ */
/* Sortable Photo Tile                                          */
/* ============================================================ */
function PhotoTile({ media, isCover, onSetCover, onDelete, onReplace, index }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: media.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const src = media.sizes?.thumbnail || media.sizes?.mobile || media.url;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square bg-[color:var(--surface)] group overflow-hidden ${isCover ? "ring-2 ring-[color:var(--accent)]" : ""}`}
      data-testid={`gm-photo-${media.id}`}
    >
      <img
        src={src}
        alt={media.alt_text || media.file_name || ""}
        className="w-full h-full object-cover"
        loading="lazy"
        style={media.dominant_color ? { backgroundColor: media.dominant_color } : undefined}
      />
      {/* Index badge */}
      <div className="absolute top-1 left-1 bg-black/60 text-[10px] px-1.5 py-0.5 tracking-wider">
        #{index + 1}
      </div>
      {/* Cover badge */}
      {isCover && (
        <div className="absolute top-1 right-1 bg-[color:var(--accent)] text-black text-[10px] px-1.5 py-0.5 tracking-wider flex items-center gap-1 font-semibold">
          <Star size={10} fill="currentColor" /> COVER
        </div>
      )}
      {/* File name overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-[10px] px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
        {media.file_name || media.original_filename || "unnamed"}
        {media.width && media.height && (
          <span className="text-[color:var(--text-muted)] ml-1">· {media.width}×{media.height}</span>
        )}
      </div>
      {/* Hover controls */}
      <div className="absolute inset-0 flex items-center justify-center gap-1 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          {...attributes} {...listeners}
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
          data-testid={`gm-drag-${media.id}`}
        >
          <GripVertical size={14} />
        </button>
        {!isCover ? (
          <button
            type="button" onClick={onSetCover}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-[color:var(--accent)] hover:text-black flex items-center justify-center"
            title="Set as cover"
            data-testid={`gm-setcover-${media.id}`}
          >
            <Star size={14} />
          </button>
        ) : (
          <button
            type="button" onClick={() => onSetCover(null)}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center"
            title="Unset cover"
          >
            <StarOff size={14} />
          </button>
        )}
        <button
          type="button" onClick={onReplace}
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center"
          title="Replace"
          data-testid={`gm-replace-${media.id}`}
        >
          <RefreshCw size={13} />
        </button>
        <button
          type="button" onClick={onDelete}
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-red-500 flex items-center justify-center"
          title="Delete"
          data-testid={`gm-delete-${media.id}`}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

/* ============================================================ */
/* Sortable Video Tile                                          */
/* ============================================================ */
function VideoTile({ media, onDelete, onReplace }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: media.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const poster = media.thumbnail_url;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative aspect-video bg-black group overflow-hidden"
      data-testid={`gm-video-${media.id}`}
    >
      <video
        src={media.url}
        poster={poster}
        muted
        preload="metadata"
        className="w-full h-full object-cover"
      />
      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-[10px] px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
        {media.file_name || "video"}
        {media.duration && <span className="text-[color:var(--text-muted)] ml-1">· {Math.round(media.duration)}s</span>}
      </div>
      <div className="absolute inset-0 flex items-center justify-center gap-1 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          {...attributes} {...listeners}
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical size={14} />
        </button>
        <button
          type="button" onClick={onReplace}
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center"
          title="Replace"
        >
          <RefreshCw size={13} />
        </button>
        <button
          type="button" onClick={onDelete}
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-red-500 flex items-center justify-center"
          title="Delete"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}
